import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { getLogLevelTone } from '@shared/config/theme';
import { toneChipClasses } from '@shared/utils/statusTone';
import { Button } from '@shared/components/ui';

interface RestoreJobLog {
  id: string;
  organization_id: string;
  agent_id: string | null;
  level: string;
  source: string;
  message: string;
  metadata: any;
  created_at: string;
  restore_job_id: string;
}

interface RestoreJobLogsViewerProps {
  isOpen: boolean;
  onClose: () => void;
  restoreJobId: string;
  backupConfigName?: string;
}

export function RestoreJobLogsViewer({
  isOpen,
  onClose,
  restoreJobId,
  backupConfigName,
}: RestoreJobLogsViewerProps) {
  const { t, i18n } = useTranslation('restores');
  const locale = i18n.resolvedLanguage || i18n.language;
  const [logs, setLogs] = useState<RestoreJobLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get<{ logs: RestoreJobLog[]; count: number }>(
        `/restore/logs?job_id=${restoreJobId}`
      );
      setLogs(response.data.logs || []);
    } catch (err: any) {
      console.error('Failed to fetch restore logs:', err);
      setError(t('logs.viewer.failed'));
    } finally {
      setLoading(false);
    }
  }, [restoreJobId, t]);

  useEffect(() => {
    if (isOpen && restoreJobId) {
      fetchLogs();
    }
  }, [isOpen, restoreJobId, fetchLogs]);

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return timestamp;
    }
  };

  const handleDownloadLogs = () => {
    // Create log file content
    const logContent = logs
      .map((log) => `[${log.created_at}] [${log.level}] [${log.source}] ${log.message}`)
      .join('\n');

    // Create blob and download
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `restore-logs-${restoreJobId}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center the modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-surface-card-light dark:bg-surface-card-dark rounded-xl text-left overflow-hidden shadow-elevated transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full border border-surface-border-light dark:border-surface-border-dark">
          {/* Modal header */}
          <div className="bg-surface-2-light dark:bg-surface-2-dark px-4 py-3 sm:px-6 flex justify-between items-center border-b border-surface-border-light dark:border-surface-border-dark">
            <div className="min-w-0 flex-1">
              <h3
                className="text-lg leading-6 font-black text-slate-900 dark:text-white tracking-tight"
                id="modal-title"
              >
                {t('logs.viewer.title')}
              </h3>
              {backupConfigName && (
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1 font-medium">
                  {backupConfigName}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="min-h-11 whitespace-nowrap px-2 sm:px-3 py-2 bg-brand-600 text-white text-sm font-black uppercase tracking-wider rounded-lg hover:bg-brand-700 focus:outline-none transition-colors disabled:opacity-50"
                onClick={handleDownloadLogs}
                disabled={loading || logs.length === 0}
              >
                {t('logs.viewer.download')}
              </button>
              <button
                type="button"
                className="min-h-11 min-w-11 flex items-center justify-center bg-transparent rounded-md text-text-subtle-light dark:text-text-subtle-dark hover:text-text-muted-light dark:hover:text-slate-300 focus:outline-none p-1"
                onClick={onClose}
              >
                <span className="sr-only">{t('selective.actions.close')}</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal body */}
          <div className="px-4 py-5 sm:p-6 bg-surface-card-light dark:bg-surface-card-dark">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
                <p className="mt-4 text-text-muted-light dark:text-text-muted-dark font-medium">
                  {t('logs.viewer.loading')}
                </p>
              </div>
            ) : error ? (
              <div
                role="alert"
                className="rounded-xl bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 space-y-3"
              >
                <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                  {error}
                </p>
                <Button type="button" variant="secondary" size="md" onClick={fetchLogs}>
                  {t('logs.viewer.retry')}
                </Button>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-text-subtle-light dark:text-text-subtle-dark"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-black text-slate-900 dark:text-white uppercase tracking-wide">
                  {t('logs.noLogsAvailable')}
                </h3>
                <p className="mt-1 text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('logs.viewer.emptyDescription')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Log summary */}
                <div className="bg-surface-2-light dark:bg-surface-2-dark rounded-xl p-3 flex justify-between items-center">
                  <span className="text-sm text-slate-600 dark:text-text-subtle-dark">
                    {t('logs.viewer.count', { count: logs.length })}
                  </span>
                  <button
                    type="button"
                    className="min-h-11 px-2 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-bold uppercase tracking-wider"
                    onClick={fetchLogs}
                  >
                    {t('logs.viewer.refresh')}
                  </button>
                </div>

                {/* Logs table */}
                <div className="bg-surface-card-light dark:bg-surface-2-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl max-h-96 overflow-auto">
                  <table className="min-w-[680px] sm:min-w-full divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    <thead className="bg-surface-2-light dark:bg-surface-2-dark sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider w-40">
                          {t('logs.viewer.timestamp')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider w-20">
                          {t('logs.viewer.level')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider w-32">
                          {t('logs.viewer.source')}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-black text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                          {t('logs.viewer.message')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface-card-light dark:bg-surface-card-dark divide-y divide-surface-border-light dark:divide-surface-border-dark">
                      {logs.map((log, index) => {
                        const tone = getLogLevelTone(log.level);
                        return (
                          <tr
                            key={log.id || index}
                            className="hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-slate-600 dark:text-text-subtle-dark">
                              {formatTimestamp(log.created_at)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-4 font-black uppercase tracking-wider rounded-full ${toneChipClasses[tone]}`}
                              >
                                {log.level}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-xs font-mono text-text-muted-light dark:text-text-muted-dark">
                              {log.source}
                            </td>
                            <td className="px-4 py-2 text-sm text-slate-900 dark:text-slate-200 font-mono break-words max-w-2xl">
                              {log.message}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Modal footer */}
          <div className="bg-surface-2-light dark:bg-surface-2-dark px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-surface-border-light dark:border-surface-border-dark">
            <button
              type="button"
              className="min-h-11 w-full inline-flex justify-center rounded-xl border border-border-strong-light dark:border-border-strong-dark shadow-sm px-4 py-2 bg-surface-card-light dark:bg-surface-2-dark text-base font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 hover:bg-surface-2-light dark:hover:bg-surface-border-dark focus:outline-none sm:ml-3 sm:w-auto sm:text-sm transition-colors"
              onClick={onClose}
            >
              {t('selective.actions.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
